import { Check } from "lucide-react";
import {
  ESSENTIALS_CORE_FEATURES,
  PLAN_TIER_LABELS,
  PLAN_TIER_PER_ROOM_CENTS,
} from "@shared/modular-pricing";
import type { AccountKind, PlanTier } from "./plan-selection";

const CORE_FEATURE_LABELS: Record<(typeof ESSENTIALS_CORE_FEATURES)[number], string> = {
  properties: "Properties & rooms",
  members: "Members",
  vehicles: "Vehicles",
  booking_requests: "Booking requests",
  messages: "Member messaging",
  tasks: "Tasks & maintenance",
  todos: "To-dos",
};

/**
 * Step 2: the base tier. Labels swap by account kind (Operator/PM
 * Essentials/Pro) — the price is identical either way. Picking Pro locks
 * every module + device in as "included" downstream.
 */
export function BasePlanCards({
  accountKind,
  value,
  onChange,
}: {
  accountKind: AccountKind;
  value: PlanTier;
  onChange: (tier: PlanTier) => void;
}) {
  const essentialsActive = value === "essentials";
  const proActive = value === "pro";
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <button
        type="button"
        onClick={() => onChange("essentials")}
        className={`rounded-2xl border-2 p-5 text-left transition-all flex flex-col ${
          essentialsActive ? "border-brand bg-brand/5 shadow-md" : "border-gray-200 bg-white hover:border-gray-300"
        }`}
        aria-pressed={essentialsActive}
        data-testid="card-plan-essentials"
      >
        <div className="flex items-baseline justify-between mb-1">
          <span className="text-lg font-bold text-gray-900">{PLAN_TIER_LABELS[accountKind].essentials}</span>
          <span className="text-2xl font-extrabold text-gray-900">
            ${PLAN_TIER_PER_ROOM_CENTS.essentials / 100}
            <span className="text-sm font-normal text-gray-500">/room/mo</span>
          </span>
        </div>
        <p className="text-sm text-gray-500 mb-3">The daily-operations core. Add modules as you need them.</p>
        <ul className="text-sm text-gray-600 space-y-1.5">
          {ESSENTIALS_CORE_FEATURES.map((key) => (
            <li key={key} className="flex items-center">
              <Check size={14} className="text-emerald-500 mr-2 shrink-0" />
              {CORE_FEATURE_LABELS[key]}
            </li>
          ))}
        </ul>
      </button>

      <button
        type="button"
        onClick={() => onChange("pro")}
        className={`rounded-2xl border-2 p-5 text-left transition-all flex flex-col ${
          proActive ? "border-brand bg-brand/5 shadow-md" : "border-gray-200 bg-white hover:border-gray-300"
        }`}
        aria-pressed={proActive}
        data-testid="card-plan-pro"
      >
        <div className="flex items-baseline justify-between mb-1">
          <span className="text-lg font-bold text-gray-900">{PLAN_TIER_LABELS[accountKind].pro}</span>
          <span className="text-2xl font-extrabold text-gray-900">
            ${PLAN_TIER_PER_ROOM_CENTS.pro / 100}
            <span className="text-sm font-normal text-gray-500">/room/mo</span>
          </span>
        </div>
        <p className="text-sm text-gray-500 mb-3">
          Everything in Essentials plus EVERY module and device integration — less than à la carte.
        </p>
        <ul className="text-sm text-gray-600 space-y-1.5">
          <li className="flex items-center">
            <Check size={14} className="text-emerald-500 mr-2 shrink-0" />
            All Essentials features
          </li>
          <li className="flex items-center">
            <Check size={14} className="text-emerald-500 mr-2 shrink-0" />
            All operator modules included
          </li>
          <li className="flex items-center">
            <Check size={14} className="text-emerald-500 mr-2 shrink-0" />
            Smart locks & thermostats included
          </li>
        </ul>
      </button>
    </div>
  );
}
